import { useLanguageStore } from '@/context/LanguageStore';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import consts from '@/config/consts';
import { findProduct, findProductByValidity } from '@/helpers/wizard';

type HomepageResponse = {
  data: Elvita.HomePageData;
  status: number;
  lastModified: string;
};

export function useFetchStartPage() {
  const { data: productConfig, isLoading, isError } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  const query = useQuery<HomepageResponse>({
    queryKey: ['startpage', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/startpage.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return {
          data: response.data,
          status: response.status,
          lastModified: response.headers['last-modified'],
        };
      }),

    enabled: !!manifestId,
  });

  return {
    ...query,
    isError: isError || query.isError,
    isLoading: isLoading || query.isPending,
  };
}

export function useFetchSettings() {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['settings', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/settings.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

type BrowseResponse = { data: Elvita.Node[]; status: number };

export function useFetchBrowse(): UseQueryResult<BrowseResponse> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery<BrowseResponse>({
    queryKey: ['browse', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/browse.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return { data: response.data, status: response.status };
      }),

    enabled: !!manifestId,
  });
}

type TopicResponse = { data: string; status: number };

export function useFetchTopic(topicId: string): UseQueryResult<TopicResponse> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['topic', manifestId, topicId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/html/${topicId}.html`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return { data: response.data, status: response.status };
      }),

    enabled: !!manifestId && !!topicId,
  });
}

export function useFetchHotspots(): UseQueryResult<Elvita.Hotspot[]> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['hotspots', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/hotspots.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

type WizardStructureResponse = { data: Elvita.WizardItem[]; status: number };

export function useFetchWizardStructure(
  langOverride?: string,
): UseQueryResult<WizardStructureResponse> {
  const storeLang = useLanguageStore(state => state.lang);
  const lang = langOverride ?? storeLang;

  return useQuery({
    queryKey: ['wizard', lang],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/configs/wizard_${lang}.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        const wizardData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        return {
          data: wizardData,
          status: response.status,
        };
      }),

    enabled: !!lang,
  });
}

export function useFetchMenu(): UseQueryResult<Elvita.MenuItem[]> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['main-menu', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/menu.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

type ProductConfigResult = {
  data: Elvita.ProductConfigItem | undefined;
  isLoading: boolean;
  isPending: boolean;
  isError: boolean;
};

/**
 * Resolve the active product -> manifest WITHOUT reading productconfigs.json.
 *
 * The URL carries `{externalNo}_{lang}` purely for a human-readable link.
 * The wizard tree (already loaded for navigation) carries `validity` per
 * leaf, and manifestId == `{validity}_{lang}`, so everything productconfigs
 * used to provide (ManifestId/ProductId/KtyValidity/Lang) is reconstructed
 * from the wizard leaf + current language. productconfigs is thereby
 * demoted to a pure search-index source on the render path.
 *
 * Consequence of sharing: variants with the same `validity` (e.g. CKF1142V
 * and CKF1142X) resolve to the same manifest and behave as one product.
 */
export function useProductConfig(): ProductConfigResult {
  const { productId } = useParams<{ productId: string }>();
  const storeLang = useLanguageStore(state => state.lang);
  // The URL product id is `{externalNo}_{lang}` and the language suffix is
  // the source of truth here (externalNo never contains an underscore, and
  // every language code is 5 chars: en-GB, sv-SE, ...). Resolving against the
  // URL language — not the persisted store language — keeps deep links and
  // localhost (which defaults the store to sv-SE) working.
  const lang = productId ? productId.slice(-5) : storeLang;
  const { data: wizard, isLoading, isPending, isError } =
    useFetchWizardStructure(lang);

  // Externalnos always start with a letter, so a digit prefix means the
  // manifest form.
  const isManifestForm = !!productId && /^\d+_/.test(productId);

  const product =
    productId && wizard?.data
      ? isManifestForm
        ? findProductByValidity(wizard.data, parseInt(productId, 10))
        : findProduct(wizard.data, productId, lang)
      : undefined;

  const data: Elvita.ProductConfigItem | undefined = product
    ? {
        ManifestId: `${product.validity}_${lang}`,
        ProductId: productId as string,
        Lang: lang,
        KtyValidity: product.validity,
      }
    : undefined;

  // Wizard finished loading but the product id isn't in the tree -> not found.
  const notFound = !!productId && !!wizard?.data && !product;

  return { data, isLoading, isPending, isError: isError || notFound };
}
